import React, { useState, useEffect, useCallback } from 'react'
import inputController, { InputLockReason } from '../services/InputController'

interface UseInputLockingOptions {
  lockReason?: InputLockReason
  debugLabel?: string
  enableDebugLogs?: boolean
}

interface UseInputLockingReturn {
  inputFocused: boolean
  handleInputFocus: () => void
  handleInputBlur: () => void
  handleKeyDown: (e: React.KeyboardEvent) => void
  handleKeyUp: (e: React.KeyboardEvent) => void
  KeyboardCaptureLayer: React.FC
}

/**
 * Custom hook for managing input locking and keyboard event handling
 * 
 * This hook provides a complete solution for components that need to:
 * - Lock game input when text fields are focused
 * - Prevent keyboard events from reaching the game
 * - Provide visual feedback and debugging
 * 
 * @param options Configuration options for the hook
 * @returns Object with handlers and components needed for input locking
 * 
 * @example
 * ```tsx
 * // Example 1: Virtual Areas Plugin
 * const VirtualAreasPanel = () => {
 *   const {
 *     inputFocused,
 *     handleInputFocus,
 *     handleInputBlur,
 *     handleKeyDown,
 *     handleKeyUp,
 *     KeyboardCaptureLayer
 *   } = useInputLocking({
 *     lockReason: InputLockReason.MAP_EDITOR,
 *     debugLabel: 'VirtualAreas',
 *     enableDebugLogs: true
 *   })
 * 
 *   return (
 *     <>
 *       {inputFocused && <KeyboardCaptureLayer />}
 *       <input
 *         type="text"
 *         placeholder="Area name..."
 *         onFocus={handleInputFocus}
 *         onBlur={handleInputBlur}
 *         onKeyDown={handleKeyDown}
 *         onKeyUp={handleKeyUp}
 *       />
 *     </>
 *   )
 * }
 * 
 * // Example 2: Chat Component (using CHAT lock reason)
 * const ChatComponent = () => {
 *   const {
 *     inputFocused,
 *     handleInputFocus,
 *     handleInputBlur,
 *     handleKeyDown,
 *     handleKeyUp,
 *     KeyboardCaptureLayer
 *   } = useInputLocking({
 *     lockReason: InputLockReason.CHAT,
 *     debugLabel: 'ChatInput',
 *     enableDebugLogs: false
 *   })
 * 
 *   return (
 *     <>
 *       {inputFocused && <KeyboardCaptureLayer />}
 *       <textarea
 *         placeholder="Type a message..."
 *         onFocus={handleInputFocus}
 *         onBlur={handleInputBlur}
 *         onKeyDown={handleKeyDown}
 *         onKeyUp={handleKeyUp}
 *       />
 *     </>
 *   )
 * }
 * ```
 */
export const useInputLocking = (options: UseInputLockingOptions = {}): UseInputLockingReturn => {
  const {
    lockReason = InputLockReason.MAP_EDITOR,
    debugLabel = 'InputLocking',
    enableDebugLogs = false
  } = options

  // State to track if any input in this component is focused
  const [inputFocused, setInputFocused] = useState(false)

  // Debug logging helper
  const log = useCallback((message: string, ...args: any[]) => {
    if (enableDebugLogs) {
      console.log(`[${debugLabel}] ${message}`, ...args)
    }
  }, [debugLabel, enableDebugLogs])

  // Input focus handler
  const handleInputFocus = useCallback(() => {
    log('Input focused - locking game input')
    inputController.lockInput(lockReason)
    setInputFocused(true)
  }, [lockReason, log])

  // Input blur handler
  const handleInputBlur = useCallback(() => {
    log('Input blurred - unlocking game input')
    inputController.unlockInput(lockReason)
    setInputFocused(false)
  }, [lockReason, log])

  // Local keyboard event handlers
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    log('Key down:', e.key)
    // Stop propagation to prevent game from receiving keyboard events
    e.stopPropagation()
  }, [log])

  const handleKeyUp = useCallback((e: React.KeyboardEvent) => {
    log('Key up:', e.key)
    // Stop propagation to prevent game from receiving keyboard events
    e.stopPropagation()
  }, [log])

  // Global keyboard event handling when input is focused
  useEffect(() => {
    if (!inputFocused) return

    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      log('Global key down captured:', e.key)
      
      // Check if the target is an input field or textarea
      const target = e.target as HTMLElement
      const isInputField = target.tagName === 'INPUT' || 
                          target.tagName === 'TEXTAREA' || 
                          target.contentEditable === 'true' ||
                          target.closest('input') ||
                          target.closest('textarea')

      // Allow all keys if user is typing in an input field
      if (isInputField) {
        log('Allowing key in input field:', e.key)
        return
      }

      // Only stop propagation for specific keys that might interfere with game controls
      const keysToPrevent = ['Escape', 'Tab']
      if (keysToPrevent.includes(e.key)) {
        e.stopPropagation()
      }
    }

    const handleGlobalKeyUp = (e: KeyboardEvent) => {
      log('Global key up captured:', e.key)
      
      // Check if the target is an input field or textarea
      const target = e.target as HTMLElement
      const isInputField = target.tagName === 'INPUT' || 
                          target.tagName === 'TEXTAREA' || 
                          target.contentEditable === 'true' ||
                          target.closest('input') ||
                          target.closest('textarea')

      // Allow all keys if user is typing in an input field
      if (isInputField) {
        log('Allowing key in input field:', e.key)
        return
      }

      // Only stop propagation for specific keys that might interfere with game controls
      const keysToPrevent = ['Escape', 'Tab']
      if (keysToPrevent.includes(e.key)) {
        e.stopPropagation()
      }
    }

    // Add event listeners with capture phase to intercept events
    window.addEventListener('keydown', handleGlobalKeyDown, { capture: true })
    window.addEventListener('keyup', handleGlobalKeyUp, { capture: true })

    return () => {
      window.removeEventListener('keydown', handleGlobalKeyDown, { capture: true })
      window.removeEventListener('keyup', handleGlobalKeyUp, { capture: true })
    }
  }, [inputFocused, log])

  // Cleanup when component unmounts
  useEffect(() => {
    return () => {
      // Ensure we unlock inputs if the component is unmounted
      inputController.unlockInput(lockReason)
      log('Component unmounted - cleaning up input locks')
    }
  }, [lockReason, log])

  // Keyboard capture layer component
  const KeyboardCaptureLayer: React.FC = useCallback(() => {
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'transparent',
          zIndex: 399, // High priority overlay
          pointerEvents: 'none' // Allow clicking through
        }}
      />
    )
  }, [])

  return {
    inputFocused,
    handleInputFocus,
    handleInputBlur,
    handleKeyDown,
    handleKeyUp,
    KeyboardCaptureLayer
  }
} 